import React from "react";
import { CalendarOutlined, MedicineBoxOutlined } from "@ant-design/icons";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import { Divider, Tooltip } from "antd";
import { IAppointment } from "@base/modules/appointment/appointment.interface";
import dayjs from "dayjs";
import { EAppointmentStatus, EAppointmentType } from "@base/enums";
import TagAppointmentType from "@components/CommonStyles/TagAppointmentType";
import TagAppointmentStatus from "@components/CommonStyles/TagAppointmentStatus";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { baseRoutes } from "@base/routes/baseRoutes";
import { getAppointmentUsedService, getServiceChannel, hasServiceLocation } from "@base/modules/appointment/appointmentUsedService";

interface IProps {
  appointment: IAppointment;
}

/** One fact of the card behind its icon. Icons sit in a fixed-width slot, so every text starts at the same x. */
const InfoLine = ({ icon, className, children }: { icon: React.ReactNode; className?: string; children: React.ReactNode }) => (
  <div className="flex items-start gap-2 min-w-0">
    <span className="flex-none flex items-center justify-center w-5 h-6 text-base text-common-primary">{icon}</span>
    <div className={`min-w-0 leading-6 font-['Inter'] ${className ?? ""}`}>{children}</div>
  </div>
);

export default function AppointmentListItem({ appointment }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const usedService = getAppointmentUsedService(appointment);
  // When the service happens at the patient's home or a partner clinic, the selling site and the
  // channel "doctor" say nothing useful, so the row drops both.
  const showsServiceLocation = hasServiceLocation(usedService);
  const siteName = appointment.site_name_show || appointment.site_name;
  const doctorName = appointment.doctor_name_show || appointment.doctor?.doctor_name;
  const serviceNames = appointment.appointment_used_services
    ?.map((service) => service.name)
    .filter(Boolean)
    .join(", ");

  const bookingBtn = (
    <CommonStyles.ButtonUI
      type="primary"
      ghost
      onClick={() => navigate(baseRoutes.userAppointmentDetail.replace(":key", appointment.key ? (appointment.key as string) : ""))}
    >
      {t("user.profile.viewDetail")}
    </CommonStyles.ButtonUI>
  );

  return (
    <div className="w-full bg-common-background p-3 rounded-2xl">
      <div className="flex justify-between items-center gap-3">
        <div className="min-w-0 flex flex-col gap-0.5">
          <span className="text-black text-base font-medium font-['Inter'] line-clamp-1">{appointment.patient_record?.patient_name}</span>
          <span className="text-common-subtext text-xs font-normal font-['Inter'] line-clamp-1">
            {[
              appointment.patient_record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
              appointment.patient_record?.patient_dob ? dayjs(appointment.patient_record.patient_dob, "YYYY-MM-DD").format("DD/MM/YYYY") : null,
            ]
              .filter(Boolean)
              .join(" - ")}
          </span>
        </div>

        <div>
          <TagAppointmentStatus status={appointment.status as EAppointmentStatus} />
        </div>
      </div>

      <Divider className="my-4" />

      <div className="flex justify-between items-start gap-4">
        <div className="flex items-start gap-4 min-w-0 max-lg:w-full">
          <div className="min-w-0 flex flex-col justify-between min-h-[125px] max-sm:min-h-0 max-lg:flex-row max-lg:grow max-sm:flex-col max-sm:gap-2">
            <div className="flex flex-col gap-1 max-lg:grow">
              <div className="max-md:hidden">
                <TagAppointmentType type={appointment.appointment_type as EAppointmentType} channel={getServiceChannel(usedService)} />
              </div>

              {showsServiceLocation ? (
                // Package lines run long: two lines here, the whole name on hover.
                <Tooltip title={serviceNames} placement="topLeft">
                  <div>
                    <InfoLine icon={<MedicineBoxOutlined />} className="text-common-maintext text-base font-bold line-clamp-2 break-words">
                      {serviceNames}
                    </InfoLine>
                  </div>
                </Tooltip>
              ) : (
                <>
                  {doctorName ? (
                    <InfoLine icon={<CommonIcons.IconDoctorPerson size={18} />} className="text-common-maintext text-base font-bold max-sm:line-clamp-1">
                      {doctorName}
                    </InfoLine>
                  ) : null}

                  {/* The doctor alone does not say where to turn up. */}
                  {siteName ? (
                    <InfoLine icon={<CommonIcons.IconDoctorSite size={18} />} className="text-common-maintext text-sm font-normal line-clamp-1">
                      {siteName}
                      {appointment.site_branch_name ? ", " + appointment.site_branch_name : ""}
                    </InfoLine>
                  ) : null}
                </>
              )}

              {appointment.status === EAppointmentStatus.PENDING && !appointment.payment_status ? (
                <span>
                  <span className="text-common-red500">(Chưa thanh toán)</span>
                </span>
              ) : null}

              {/* Without the calendar tile on the right, the time and day get their own line. */}
              <div className="lg:hidden">
                <InfoLine icon={<CalendarOutlined />} className="text-common-maintext text-sm font-medium">
                  {dayjs(appointment.time_table_period?.period_start_time).format("HH:mm")}{" "}
                  {dayjs(appointment.appointment_date, "YYYY-MM-DD").format("DD/MM/YYYY")}
                </InfoLine>
              </div>
            </div>

            <div className="items-end flex-none w-fit max-lg:flex max-lg:items-center">{bookingBtn}</div>

            {/* <div className="text-common-maintext text-xs font-normal font-['Inter']">Dermatology consultation</div> */}
          </div>
        </div>

        <div className="max-lg:hidden">
          <CommonStyles.DayCalendarUI value={dayjs(appointment.appointment_date, "YYYY-MM-DD").format("DD/MM/YYYY")} format="DD/MM/YYYY" />
        </div>
      </div>
    </div>
  );
}
